import { formatStatus } from "./statusLabels";

const SERVICE_TYPE_LABELS = {
  FULL_TIME: "Full-time care at home",
  FULLTIME: "Full-time care at home",
  HOURLY: "Hourly visit",
  "12_DAY": "12 hours (day)",
  "12_NIGHT": "12 hours (night)",
  "24H": "24 hours live-in",
  "24_HOURS_LIVE_IN": "24 hours live-in",
  "12_HOURS_DAY": "12 hours (day)",
  "12_HOURS_NIGHT": "12 hours (night)",
};

const KYC_STATUS_LABELS = {
  PENDING: "Not submitted yet",
  SUBMITTED: "Submitted — under review",
  IN_REVIEW: "Under review",
  VERIFIED: "Verified",
  APPROVED: "Verified",
  COMPLETE: "Verified",
  REJECTED: "Needs correction",
  FAILED: "Could not verify",
};

const SUBSCRIPTION_STATUS_LABELS = {
  ACTIVE: "Your plan is active",
  "ACTIVE (MONTH 1 FROM DEPOSIT)": "Active — first month included",
  "MONTH 2 DUE": "Monthly fee due",
  AWAITING_TRIAL: "Pay security deposit to start",
  EXPIRED: "Plan expired — renew to continue",
  DUE: "Payment due",
  EXPIRING: "Renewal due soon",
  PAID: "Paid",
  INACTIVE: "Not active",
};

const BILLING_LINE_LABELS = {
  CUSTOMER_PLATFORM_SUBSCRIPTION: "Monthly platform plan",
  CUSTOMER_SUBSCRIPTION: "Monthly platform plan",
  CUSTOMER_CARE_PASS: "Care Pass",
  CUSTOMER_MARKETPLACE_ACCESS: "Marketplace access",
  PATIENT_SUBSCRIPTION: "Patient subscription",
  CAREGIVER_REACTIVATION: "Caregiver listing renewal",
  CARE_MANAGER_SUBSCRIPTION: "Care manager listing",
  FULL_TIME_SECURITY_DEPOSIT: "Security deposit",
  FULL_TIME_POLICE_VERIFICATION: "Police verification fee",
  FULL_TIME_ENGAGEMENT_DEPOSIT: "Engagement deposit",
  FULL_TIME_ENGAGEMENT_POLICE: "Police verification (engagement)",
  POLICE_VERIFICATION_NEW_ENGAGEMENT: "Police verification",
  CUSTOMER_POLICE_RECORD_VERIFICATION: "Police verification",
  HOURLY_BOOKING_ESTIMATE: "Hourly visit estimate",
  HOURLY_POLICE_VERIFICATION: "Police verification (hourly)",
  HOURLY_PLATFORM_COMPONENT: "Platform service fee",
  HOURLY_TRAVEL_PASS_THROUGH: "Travel allowance",
  HOURLY_CAREGIVER_SETTLEMENT: "Caregiver payment",
  PLATFORM_BOOKING_FEE: "Booking facilitation fee",
  INTRO_CALL_WALLET_USAGE: "Intro call usage",
  TALK_WALLET_RECHARGE: "Talk time added",
  ASSISTED_MATCHMAKING: "Assisted matching",
  TALK_WALLET_MANUAL_CREDIT: "Talk time credit",
  WALLET_REFUND: "Refund",
};

const METRIC_LABELS = {
  newRegistrationsToday: "New sign-ups today",
  activeSubscribers: "Active members",
  expiringSubscriptions: "Plans expiring soon",
  newCaregiverRegistrations: "New caregivers",
  pendingVerification: "Awaiting verification",
  approvedProfiles: "Approved caregivers",
  searches: "Care searches",
  profileViews: "Profile views",
  contactRequests: "Contact requests",
  successfulHires: "Successful hires",
  subscriptionRevenue: "Subscription income",
  renewalRevenue: "Renewal income",
  pendingPayments: "Pending payments",
  complaints: "Open complaints",
  suspensions: "Suspended accounts",
  fraudAlerts: "Safety alerts",
  Registrations: "New sign-ups",
  Verifications: "Verifications pending",
  Subscriptions: "Active plans",
  Revenue: "Income",
  "Conversion Rate": "Search to hire rate",
  "Hiring Rate": "Hires completed",
  Complaints: "Open complaints",
  "NCID Changes": "Profile score updates",
  "Marketplace Growth": "Marketplace activity",
  Retention: "Members staying active",
  Churn: "Members who left",
  "Quality Metrics": "Care quality score",
};

const TEAM_LABELS = {
  verification: "Verification team",
  support: "Support team",
  operations: "Operations team",
};

const KPI_GROUP_LABELS = {
  verification: "Verification",
  marketplace: "Marketplace",
  quality: "Quality & safety",
  revenue: "Income",
};

const NCID_ACTION_LABELS = {
  recalculate: "Score recalculated",
  lock: "Score locked",
  flag: "Score flagged for review",
};

const EXCEPTION_TYPE_LABELS = {
  COMPLAINT: "Complaint",
  DISPUTE: "Dispute",
  OPS_ESCALATION: "Ops escalation",
  REPLACEMENT: "Replacement request",
  QUALITY: "Quality issue",
  BILLING: "Billing issue",
};

const ADMIN_FORMAT_COLUMNS = new Set([
  "status",
  "subscriptionStatus",
  "verificationStatus",
  "profileStatus",
  "engagementStatus",
  "paymentStatus",
  "severity",
  "priority",
  "kycStatus",
  "aadhaarKycStatus",
  "documentsStatus",
  "serviceType",
  "type",
  "lineCode",
  "action",
  "category",
  "plan",
]);

function upperKey(value) {
  return String(value || "").trim().toUpperCase().replace(/[\s-]+/g, "_");
}

export function formatServiceType(value) {
  if (value == null || value === "") return "—";
  const key = upperKey(value);
  if (SERVICE_TYPE_LABELS[key]) return SERVICE_TYPE_LABELS[key];
  const pretty = formatStatus(value);
  return pretty || "Care service";
}

export function formatKycStatus(value) {
  if (value == null || value === "") return "Not submitted yet";
  const key = upperKey(value);
  return KYC_STATUS_LABELS[key] || formatStatus(value) || "In progress";
}

export function formatSubscriptionStatus(value) {
  if (value == null || value === "") return "—";
  const raw = String(value).trim();
  const key = raw.toUpperCase();
  if (SUBSCRIPTION_STATUS_LABELS[key]) return SUBSCRIPTION_STATUS_LABELS[key];
  if (SUBSCRIPTION_STATUS_LABELS[upperKey(raw)]) return SUBSCRIPTION_STATUS_LABELS[upperKey(raw)];
  return formatStatus(raw) || raw;
}

export function formatBillingLineCode(value) {
  if (value == null || value === "") return "—";
  const key = upperKey(value);
  if (BILLING_LINE_LABELS[key]) return BILLING_LINE_LABELS[key];
  return formatStatus(value) || "Service fee";
}

export function formatYesNo(value) {
  if (value === true || String(value).toLowerCase() === "true") return "Yes";
  if (value === false || String(value).toLowerCase() === "false") return "No";
  return formatDisplayValue("status", value);
}

export function formatMetricLabel(key) {
  if (key == null || key === "") return "";
  return METRIC_LABELS[key] || formatStatus(String(key).replace(/([a-z])([A-Z])/g, "$1 $2")) || String(key);
}

export function formatTeamLabel(key) {
  return TEAM_LABELS[key] || formatStatus(key) || String(key);
}

export function formatKpiGroupLabel(key) {
  return KPI_GROUP_LABELS[key] || formatStatus(key) || String(key);
}

export function formatNcidAction(value) {
  if (value == null || value === "") return "—";
  const key = String(value).trim().toLowerCase();
  return NCID_ACTION_LABELS[key] || formatStatus(value) || String(value);
}

export function formatNpidAction(value) {
  return formatNcidAction(value);
}

export function formatExceptionType(value) {
  if (value == null || value === "") return "—";
  const key = String(value).trim().toUpperCase();
  if (EXCEPTION_TYPE_LABELS[key]) return EXCEPTION_TYPE_LABELS[key];
  return formatStatus(value) || String(value);
}

export function formatDisplayValue(columnKey, value) {
  if (value == null || value === "") return "—";
  if (typeof value === "boolean") return formatYesNo(value);

  const col = String(columnKey || "");
  const colLower = col.toLowerCase();

  if (colLower === "servicetype" || colLower === "type" || colLower === "bookingtype") {
    return formatServiceType(value);
  }
  if (colLower === "linecode" || colLower === "line_code") {
    return formatBillingLineCode(value);
  }
  if (colLower === "action") {
    return formatNpidAction(value);
  }
  if (colLower === "leadsource") {
    return value || "Direct Website Visit";
  }
  if (colLower.includes("kyc")) {
    return formatKycStatus(value);
  }
  if (colLower.includes("subscription")) {
    return formatSubscriptionStatus(value);
  }
  if (colLower.includes("payment") && colLower.includes("status")) {
    return formatStatus(value) || "—";
  }
  if (ADMIN_FORMAT_COLUMNS.has(col) || colLower.endsWith("status")) {
    return formatStatus(value) || String(value);
  }
  if (typeof value === "string" && /^[A-Z0-9_]+$/.test(value) && value.includes("_")) {
    return formatStatus(value);
  }
  return value;
}

export function formatCaregiverLevel(value) {
  if (value == null || value === "") return "Registered caregiver";
  const raw = String(value).trim();
  if (/level\s*\d/i.test(raw) || raw.includes("—") || raw.includes("•")) {
    return raw.replace(/\s*[—–-]\s*/g, " • ");
  }
  const num = Number(raw);
  if (Number.isFinite(num) && num >= 1 && num <= 5) {
    const names = {
      1: "Level 1 • Registered",
      2: "Level 2 • Verified",
      3: "Level 3 • Experienced",
      4: "Level 4 • Premium",
      5: "Level 5 • Elite",
    };
    return names[num] || `Level ${num}`;
  }
  return formatStatus(raw) || raw;
}

/** Sidebar/header label: keep trailing numeric suffixes ("careattendent 1" → "careattendent1"). */
export function formatProfileTriggerName(name, fallback = "User") {
  const full = String(name || "").trim();
  if (!full) return fallback;
  const parts = full.split(/\s+/).filter(Boolean);
  if (parts.length === 2 && /^\d+$/.test(parts[1])) {
    return `${parts[0]}${parts[1]}`;
  }
  return full;
}

export function formatPaymentStatus(value) {
  return formatStatus(value) || "—";
}
